import { useState, useEffect } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LogOut, Settings, KeyRound, X, Menu, Search, Stethoscope, BookOpen, Mail, UserPlus, LogIn, ShieldCheck } from "lucide-react";
import logoEthos from "../../assets/images/logo-ethos.jpg";

interface UserProps {
  id: number;
  email_address: string;
  role: string;
  first_name?: string;
  last_name?: string;
}

interface PageProps {
  auth?: {
    user?: UserProps;
  };
  [key: string]: any;
}

const Header = () => {
  const { auth } = usePage<PageProps>().props;
  const isAuthenticated = !!auth?.user;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Subtle shadow on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const getUserInitials = () => {
    const user = auth?.user;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.email_address) {
      return user.email_address.charAt(0).toUpperCase();
    }
    return "U";
  };

  const userInitials = getUserInitials();

  const navLinkClass =
    "relative text-sm font-medium text-gray-600 hover:text-[#008788] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#008788] after:transition-all after:duration-200";

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b transition-shadow duration-200 ${
        scrolled ? "border-gray-200 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0">
            <img
              src={logoEthos}
              alt="Ethos Santé"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link href="/search" className={navLinkClass}>
              <span className="flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5" />
                Rechercher
              </span>
            </Link>
            <Link href="/pour-les-kines" className={navLinkClass}>
              <span className="flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5" />
                Espace kiné
              </span>
            </Link>
            <Link href="/blog" className={navLinkClass}>
              <span className="flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                Blog
              </span>
            </Link>
            <Link href="/contact" className={navLinkClass}>
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Contact
              </span>
            </Link>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-200" />

            {!isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link
                  href="/session/new"
                  className="text-sm font-medium text-gray-600 hover:text-[#008788] transition-colors"
                >
                  Se connecter
                </Link>
                <Link
                  href="/registration/new"
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-[#008788] hover:bg-[#015963] shadow-sm hover:shadow transition-all duration-200"
                >
                  S'inscrire
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {(auth?.user as any)?.role === "admin" && (
                  <Link
                    href="/admin"
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Admin
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center focus:outline-none focus:ring-2 focus:ring-[#008788] focus:ring-offset-2 rounded-full transition-transform hover:scale-105">
                      <div className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-gray-200 hover:ring-[#008788] transition-all">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userInitials)}&background=008788&color=fff&size=36`}
                          alt="Profile"
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23008788'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'%3E%3C/path%3E%3C/svg%3E";
                          }}
                        />
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-60 rounded-xl shadow-lg border border-gray-100" align="end" forceMount>
                    <div className="flex items-center gap-3 p-3 border-b border-gray-100">
                      <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-gray-100 flex-shrink-0">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userInitials)}&background=008788&color=fff&size=40`}
                          alt="Profile"
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23008788'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'%3E%3C/path%3E%3C/svg%3E";
                          }}
                        />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{auth?.user?.email_address}</p>
                        <p className="text-xs text-gray-400">Mon compte</p>
                      </div>
                    </div>
                    <div className="p-1">
                      <DropdownMenuItem asChild>
                        <Link href="/dashboard/physiotherapist" className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
                          <Settings className="mr-2.5 h-4 w-4 text-gray-400" />
                          Mon espace
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/compte/mot-de-passe" className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">
                          <KeyRound className="mr-2.5 h-4 w-4 text-gray-400" />
                          Modifier le mot de passe
                        </Link>
                      </DropdownMenuItem>
                      <div className="my-1 border-t border-gray-100" />
                      <DropdownMenuItem asChild>
                        <Link
                          href="/session"
                          method="delete"
                          as="button"
                          className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <LogOut className="mr-2.5 h-4 w-4" />
                          Se déconnecter
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3 space-y-1">
            {[
              { href: "/search", label: "Rechercher", icon: <Search className="h-4 w-4" /> },
              { href: "/pour-les-kines", label: "Espace kiné", icon: <Stethoscope className="h-4 w-4" /> },
              { href: "/blog", label: "Blog", icon: <BookOpen className="h-4 w-4" /> },
              { href: "/contact", label: "Contact", icon: <Mail className="h-4 w-4" /> },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <span className="text-gray-400">{item.icon}</span>
                {item.label}
              </Link>
            ))}

            <div className="my-2 border-t border-gray-100" />

            {!isAuthenticated ? (
              <div className="space-y-2 pt-1">
                <Link
                  href="/session/new"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <LogIn className="h-4 w-4 text-gray-400" />
                  Se connecter
                </Link>
                <Link
                  href="/registration/new"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-[#008788] hover:bg-[#015963] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <UserPlus className="h-4 w-4" />
                  S'inscrire
                </Link>
              </div>
            ) : (
              <div className="space-y-1 pt-1">
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="h-8 w-8 rounded-full overflow-hidden ring-2 ring-gray-100 flex-shrink-0">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userInitials)}&background=008788&color=fff&size=32`}
                      alt="Profile"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-gray-500 truncate">{auth?.user?.email_address}</p>
                </div>
                {(auth?.user as any)?.role === "admin" && (
                  <Link
                    href="/admin"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Admin
                  </Link>
                )}
                <Link
                  href="/dashboard/physiotherapist"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <Settings className="h-4 w-4 text-gray-400" />
                  Mon espace
                </Link>
                <Link
                  href="/compte/mot-de-passe"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <KeyRound className="h-4 w-4 text-gray-400" />
                  Modifier le mot de passe
                </Link>
                <div className="my-1 border-t border-gray-100" />
                <Link
                  href="/session"
                  method="delete"
                  as="button"
                  className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <LogOut className="h-4 w-4" />
                  Se déconnecter
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
